import { BOX_TRUCK_DOC_ALLOWED_MIME, BOX_TRUCK_DOC_MAX_BYTES, BOX_TRUCK_DOC_MAX_MEGABYTES } from '@numeo/types-core'

const ALLOWED_MIME_TYPES = new Set(BOX_TRUCK_DOC_ALLOWED_MIME)

const documentFormatLabel = (mimeType: string): string => {
    const subtype = mimeType.split('/').pop()?.toLowerCase() ?? mimeType
    if (subtype === 'jpg') return 'JPEG'
    if (subtype === 'quicktime') return 'MOV'
    return subtype.toUpperCase()
}

const formatList = (items: string[]): string => {
    if (items.length < 2) return items[0] ?? ''
    if (items.length === 2) return `${items[0]} or ${items[1]}`
    return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`
}

export const BOX_TRUCK_DOCUMENT_ACCEPT = BOX_TRUCK_DOC_ALLOWED_MIME.join(',')
export const BOX_TRUCK_DOCUMENT_FORMATS = formatList([...new Set(BOX_TRUCK_DOC_ALLOWED_MIME.map(documentFormatLabel))])
const isBoxTruckDocumentMimeAllowed = (mimeType: string): boolean => ALLOWED_MIME_TYPES.has(mimeType)

/** Why a picked file cannot be uploaded, as a bounded value for analytics, or null when it can. Type is checked first. */
export const boxTruckDocumentRejectionReason = (file: Pick<File, 'type' | 'size'>): 'unsupported_type' | 'too_large' | 'empty' | null => {
    if (!isBoxTruckDocumentMimeAllowed(file.type)) return 'unsupported_type'
    if (file.size > BOX_TRUCK_DOC_MAX_BYTES) return 'too_large'
    // An empty file would be saved and listed like any other document, and open as nothing.
    return file.size <= 0 ? 'empty' : null
}

/**
 * Why a picked file cannot be uploaded, worded to follow "<file name> was skipped: ", or null when it can.
 *
 * Checked in the browser, before any request: a file over the limit is otherwise refused by the platform in
 * front of the API, whose answer the browser cannot read, so the operator saw only "Network Error".
 */
export const boxTruckDocumentRejection = (file: Pick<File, 'type' | 'size'>): string | null => {
    const reason = boxTruckDocumentRejectionReason(file)
    if (reason === 'unsupported_type') {
        const what = file.type ? `unsupported file type (${file.type})` : 'file type could not be determined'
        return `${what}. Use ${BOX_TRUCK_DOCUMENT_FORMATS}`
    }
    if (reason === 'empty') return 'the file is empty'
    if (reason === 'too_large') {
        const megabytes = (file.size / (1024 * 1024)).toFixed(1)
        // A file a few bytes over rounds to the limit itself, and "it is 50.0 MB and the maximum is 50 MB" reads as a mistake.
        return Number(megabytes) > BOX_TRUCK_DOC_MAX_MEGABYTES
            ? `it is ${megabytes} MB and the maximum is ${BOX_TRUCK_DOC_MAX_MEGABYTES} MB`
            : `it is just over the maximum of ${BOX_TRUCK_DOC_MAX_MEGABYTES} MB`
    }
    return null
}
